import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';

import _ from 'helpers/no_conflict_lodash';
import MediaLinks from 'bundles/project/components/media_tab/media_links';
import MediaPhotoForm from 'bundles/project/components/media_tab/media_photo_form';
import VisualMediaForm from 'bundles/project/components/media_tab/visual_media_form';
import { MediaPropTypes } from 'bundles/project/components/media_tab/media_props_types';
import {
  normalizeLeaseFloorPlan,
  normalizePhotoAttachmentsData,
  normalizeVisualMediaEmbedsData
} from 'bundles/project/components/media_tab/helpers/normalizers';
import LeaseSpacesMedia from 'bundles/project/components/media_tab/lease_spaces/lease_spaces_media';
import { ProjectPropTypes } from 'components/project/prop_types';
import { get, put } from 'helpers/api';
import './styles.scss';
import PropTypes from 'prop-types';
import { UPDATE_VISUAL_MEDIA } from '../../reducers/visual_media';
import { hideActionIndicator, showActionIndicator } from 'bundles/project/actions';
import { Prompt } from 'react-router-dom';
import buildAirbrakeNotifier from '../../../../helpers/airbrake_notifier';
import { createConsumer } from '@rails/actioncable';

const findPropertyField = require('../../../../../assets/javascripts/otter/properties/find_property_field');

export function MediaTab({
  airbrake,
  hideActionIndicator,
  project,
  showActionIndicator,
  syndication,
  updateVisualMedia,
  visualMediaData
}) {
  const getMediaLinksValues = data => ({
    youTubeUrl: data.youTubeUrl || '',
    matterportUrl: data.matterportUrl || '',
    realvisionUrl: data.realvisionUrl || '',
    maprightEmbedCode: data.maprightEmbedCode || '',
    terrastrideEmbedCode: data.terrastrideEmbedCode || ''
  });

  const [initialData, setInitialData] = useState(visualMediaData);
  const [initialMediaLinksValues, setInitialMediaLinksValues] = useState(getMediaLinksValues(initialData));
  const [mediaLinksFormFields, setMediaLinksFormFields] = useState(initialMediaLinksValues);
  const [photoAttachments, setPhotoAttachments] = useState(initialData.photoProps.photoAttachments || []);
  const [visualMediaEmbeds, setVisualMediaEmbeds] = useState(initialData.visualMediaEmbeds);
  const [visualMediaEmbedsForCustomer, setVisualMediaEmbedsForCustomer] = useState(
    initialData.visualMediaEmbedsForCustomer
  );
  const [leaseSpacesMedia, setLeaseSpacesMedia] = useState(initialData.leaseSpacesMedia);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [hasHTTPErrors, setHasHTTPErrors] = useState(false);
  const [validationErrors, setValidationErrors] = useState(null);
  const [imageTooLarge, setImageTooLarge] = useState(false);
  const removeProcessingTimeouts = useRef({});

  const airbrakeNotifier = buildAirbrakeNotifier(airbrake.projectId, airbrake.apiKey, airbrake.environment);

  const checkIfDirty = (mediaLinks, propertyPhotoAttachments, propertyMediaEmbeds, leaseMediaEmbeds) => {
    let visualMediaEmbedsEqual;

    if (initialData.ovalroomGroupAdmin) {
      visualMediaEmbedsEqual = _.isEqual(initialData.visualMediaEmbeds, propertyMediaEmbeds);
    } else {
      visualMediaEmbedsEqual = _.isEqual(initialData.visualMediaEmbedsForCustomer, propertyMediaEmbeds);
    }

    const propertyPhotoAttachmentsWithoutProcessing = propertyPhotoAttachments.map((photoAttachment) => {
      const { processing, sidekiqError, sidekiqJobId, sidekiqStatus, ...everythingElse } = photoAttachment;
      return everythingElse;
    });

    const initialPhotoAttachmentsWithoutProcessing = initialData.photoProps.photoAttachments.map((photoAttachment) => {
      const { sidekiqError, sidekiqJobId, sidekiqStatus, ...everythingElse } = photoAttachment;
      return everythingElse;
    });

    const leaseSpaceMediaWithoutProcessing = leaseMediaEmbeds.map((leaseSpace) => {
      const photoAttachments = leaseSpace.photoProps.photoAttachments.map((photoAttachment) => {
        const { processing, ...everythingElse } = photoAttachment;
        return everythingElse;
      });

      return { ...leaseSpace, photoProps: { ...leaseSpace.photoProps, photoAttachments } };
    });

    const dirty = !(
      _.isEqual(initialMediaLinksValues, mediaLinks) &&
      _.isEqual(initialPhotoAttachmentsWithoutProcessing, propertyPhotoAttachmentsWithoutProcessing) &&
      visualMediaEmbedsEqual &&
      _.isEqual(initialData.leaseSpacesMedia, leaseSpaceMediaWithoutProcessing)
    );

    setDirty(dirty);
  };

  const getVisualMediaEmbedsForUser = () => {
    return initialData.ovalroomGroupAdmin ? visualMediaEmbeds : visualMediaEmbedsForCustomer;
  };

  const showVisualMediaEmbeds = () => {
    return initialData.ovalroomGroupAdmin || getVisualMediaEmbedsForUser().length > 0;
  };

  const prepareMediaDataForLeaseSpaces = () => {
    const mediaEmbedsKeyForUser = initialData.ovalroomGroupAdmin ? 'visualMediaEmbeds' : 'visualMediaEmbedsForCustomer';

    return leaseSpacesMedia.map((leaseSpace) => {
      const { photoAttachments, defaultPhotoOption } = leaseSpace.photoProps;

      return ({
        id: leaseSpace.id,
        visualMediaEmbedsAttributes: normalizeVisualMediaEmbedsData(leaseSpace[mediaEmbedsKeyForUser]),
        photoAttachmentsAttributes: normalizePhotoAttachmentsData(photoAttachments, defaultPhotoOption),
        videoUrl: leaseSpace.videoUrl,
        virtualTourUrl: leaseSpace.virtualTourUrl,
        matterportUrl: leaseSpace.matterportUrl,
        floorPlanAttributes: normalizeLeaseFloorPlan(leaseSpace.floorPlan)
      });
    });
  };

  const getVisualMediaData = () => {
    get(`/properties/${project.id}/visual_media_data`)
      .then((response) => {
        setInitialData(response);
        updateVisualMedia(response);
        setInitialMediaLinksValues(getMediaLinksValues(response));
        setMediaLinksFormFields(getMediaLinksValues(response));
        setPhotoAttachments(response.photoProps.photoAttachments || []);
        setVisualMediaEmbeds(response.visualMediaEmbeds);
        setVisualMediaEmbedsForCustomer(response.visualMediaEmbedsForCustomer);
        setLeaseSpacesMedia(response.leaseSpacesMedia);
      })
      .catch(() => {
        // Catch error
      });
  };

  const removeErrorClass = () => {
    document.querySelectorAll('.has-error').forEach((element) => {
      element.classList.remove('has-error');
    });
  };

  const handleMediaLinkFormChange = (field, newValue) => {
    const newFormValue = { ...mediaLinksFormFields, [field]: newValue };
    setMediaLinksFormFields(newFormValue);
    checkIfDirty(newFormValue, photoAttachments, getVisualMediaEmbedsForUser(), leaseSpacesMedia);
  };

  const handlePhotoChange = (newPhotoAttachments) => {
    setPhotoAttachments(newPhotoAttachments);
    checkIfDirty(mediaLinksFormFields, newPhotoAttachments, getVisualMediaEmbedsForUser(), leaseSpacesMedia);
  };

  const handleVisualMediaChange = (newVisualMediaEmbeds) => {
    setVisualMediaEmbeds(newVisualMediaEmbeds);
    checkIfDirty(mediaLinksFormFields, photoAttachments, newVisualMediaEmbeds, leaseSpacesMedia);
  };

  const handleVisualMediaForCustomerChange = (newVisualMediaEmbeds) => {
    setVisualMediaEmbedsForCustomer(newVisualMediaEmbeds);
    checkIfDirty(mediaLinksFormFields, photoAttachments, newVisualMediaEmbeds, leaseSpacesMedia);
  };

  const handleVisualMediaForLeaseChange = (newVisualMediaEmbeds, leaseId) => {
    const newMediaForLeaseSpaces = leaseSpacesMedia.map((item) => {
      if (item.id === leaseId) {
        return { ...item, visualMediaEmbeds: newVisualMediaEmbeds };
      }

      return item;
    });

    setLeaseSpacesMedia(newMediaForLeaseSpaces);
    checkIfDirty(mediaLinksFormFields, photoAttachments, getVisualMediaEmbedsForUser(), newMediaForLeaseSpaces);
  };

  const handleVisualMediaForCustomerForLeaseChange = (newVisualMediaEmbeds, leaseId) => {
    const newMediaForLeaseSpaces = leaseSpacesMedia.map((item) => {
      if (item.id === leaseId) {
        return { ...item, visualMediaEmbedsForCustomer: newVisualMediaEmbeds };
      }

      return item;
    });

    setLeaseSpacesMedia(newMediaForLeaseSpaces);
    checkIfDirty(mediaLinksFormFields, photoAttachments, getVisualMediaEmbedsForUser(), newMediaForLeaseSpaces);
  };

  const handleLeasePhotoChange = (newPhotoAttachments, leaseId) => {
    const newMediaForLeaseSpaces = leaseSpacesMedia.map((item) => {
      if (item.id === leaseId) {
        return { ...item, photoProps: { ...item.photoProps, photoAttachments: newPhotoAttachments } };
      }

      return item;
    });

    setLeaseSpacesMedia(newMediaForLeaseSpaces);
    checkIfDirty(mediaLinksFormFields, photoAttachments, getVisualMediaEmbedsForUser(), newMediaForLeaseSpaces);
  };

  const handleLeaseMediaLinksChange = (formKey, newValue, leaseId) => {
    const newMediaForLeaseSpaces = leaseSpacesMedia.map((item) => {
      if (item.id === leaseId) {
        return { ...item, [formKey]: newValue };
      }

      return item;
    });

    setLeaseSpacesMedia(newMediaForLeaseSpaces);
    checkIfDirty(mediaLinksFormFields, photoAttachments, getVisualMediaEmbedsForUser(), newMediaForLeaseSpaces);
  };

  const handleSave = async () => {
    setSaving(true);
    setHasHTTPErrors(false);
    setImageTooLarge(false);
    setValidationErrors(null);
    const { defaultPhotoOption } = initialData.photoProps;
    const params = {
      property: {
        ...mediaLinksFormFields,
        photoAttachmentsAttributes: normalizePhotoAttachmentsData(photoAttachments, defaultPhotoOption),
        visualMediaEmbedsAttributes: normalizeVisualMediaEmbedsData(getVisualMediaEmbedsForUser()),
        leaseSpacesAttributes: prepareMediaDataForLeaseSpaces()
      },
      mediaOnly: true
    };
    removeErrorClass();

    try {
      // First request for running validations
      const validationRequest = await put(`/properties/${project.id}`, { ...params, validate: 'true' });

      if (validationRequest.errors.length) {
        setValidationErrors(validationRequest.errors);

        validationRequest.errors.forEach((error) => {
          const input = findPropertyField(error.idx, error.assoc, error.attrName)?.closest('.form-group');
          input[0]?.classList.add('has-error');
        });
      } else {
        // Second request to finally save the updates
        const result = await put(`/properties/${project.id}`, params);

        setDirty(false);
        setShowSaved(true);
        getVisualMediaData();

        if (result.emailSyndicationPending) {
          showActionIndicator('syndication');
        } else {
          hideActionIndicator('syndication');
        }

        setTimeout(() => {
          setShowSaved(false);
        }, 5000);
      }
      setSaving(false);
    } catch (error) {
      if (error.response.status === 422) {
        return handleLargeImageError(error);
      }
      if (airbrake.environment !== 'development' && airbrakeNotifier) {
        airbrakeNotifier.notify({
          error: error,
          params: {
            info: `Error occured while attempting to save media on the media tab for project_id: ${project.id}`
          }
        });
      }
      setSaving(false);
      setHasHTTPErrors(true);
    }
  };

  const handleLargeImageError = (error) => {
    if (airbrake.environment !== 'development' && airbrakeNotifier) {
      airbrakeNotifier.notify({
        error: error,
        params: {
          info: `Paperclip failed to process image, image too large for project_id: ${project.id}`
        }
      });
    }
    setSaving(false);
    setImageTooLarge(true);
  };

  const handleCancel = () => {
    setMediaLinksFormFields(initialMediaLinksValues);
    setPhotoAttachments(initialData.photoProps.photoAttachments || []);
    setVisualMediaEmbeds(initialData.visualMediaEmbeds);
    setVisualMediaEmbedsForCustomer(initialData.visualMediaEmbedsForCustomer);
    setLeaseSpacesMedia(initialData.leaseSpacesMedia);
    setDirty(false);
    setShowSaved(false);
    setHasHTTPErrors(false);
    setImageTooLarge(false);
    setValidationErrors(null);
    removeErrorClass();
  };

  useEffect(() => {
    const consumer = createConsumer();
    const channel = consumer.subscriptions.create(
      {
        channel: 'SidekiqStatusChannel',
        property_id: project.id
      },
      {
        received: ({
          attachable_id: attachableId,
          attachable_type: attachableType,
          status,
          photo_id: photoId,
          thumbnail_url: thumbnailUrl,
          url
        }) => {
          function updatePhotoAttachments(prevPhotoAttachments) {
            return prevPhotoAttachments.map((photoAttachment) => {
              if (photoAttachment.photoId === photoId) {
                const fieldsToSet = { sidekiqStatus: status, processing: true };
                if (status === 'complete') {
                  fieldsToSet.sidekiqJobId = null;
                  fieldsToSet.thumbnailUrl = thumbnailUrl;
                  fieldsToSet.url = url;
                }

                return { ...photoAttachment, ...fieldsToSet };
              }

              return photoAttachment;
            });
          }

          if (attachableType === 'LeaseSpace') {
            setLeaseSpacesMedia(leaseSpaces => leaseSpaces.map((leaseSpace) => {
              if (leaseSpace.id === attachableId) {
                return {
                  ...leaseSpace,
                  photoProps: {
                    ...leaseSpace.photoProps,
                    photoAttachments: updatePhotoAttachments(leaseSpace.photoProps.photoAttachments)
                  }
                };
              }

              return leaseSpace;
            }));
          } else {
            setPhotoAttachments(updatePhotoAttachments);
          }
        }
      }
    );

    return () => {
      channel.unsubscribe();
      consumer.disconnect();
    };
  }, []);

  //TODO: Refactor this useEffect to bulk update the photoAttachment processing state
  //to false. Check Kelvin's comment in BSD-31810
  useEffect(() => {
    function removeProcessingFromPhoto(photoAttachment) {
      setPhotoAttachments(prevState => prevState.map((pa) => {
        if (pa.photoId === photoAttachment.photoId) {
          return { ...pa, processing: false };
        }

        return pa;
      }));

      delete removeProcessingTimeouts.current[photoAttachment.photoId];
    }

    photoAttachments.forEach((photoAttachment) => {
      if (photoAttachment.sidekiqStatus === 'complete' &&
          photoAttachment.processing === true &&
          !removeProcessingTimeouts.current[photoAttachment.photoId]) {
        removeProcessingTimeouts.current[photoAttachment.photoId] = setTimeout(() => {
          removeProcessingFromPhoto(photoAttachment);
        }, 2000);
      }
    });
  }, [photoAttachments]);

  useEffect(() => {
    return () => {
      Object.values(removeProcessingTimeouts.current).forEach(clearTimeout);
    };
  }, []);

  useEffect(() => {
    const onUnload = (event) => {
      if (dirty) event.returnValue = true;
    };

    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [dirty]);

  return (
    <React.Fragment>
      <Prompt
        message="You have unsaved changes, Leaving the media tab will undo any changes"
        when={dirty}
      />
      <div className="panel panel-default">
        <div className="p-5 mt-1 panel-body">
          <div className="media-header d-flex align-items-center justify-content-center">
            <h2 className="m-0">
              Media
            </h2>
            <div className="action-buttons-container d-flex align-items-center">
              {showSaved && (
                <label className="text-green mr-3">Successfully saved!</label>
              )}
              {hasHTTPErrors && (
                <label className="text-danger mr-3">
                  Property could not be updated. Please try again or contact support for help.
                </label>
              )}
              {imageTooLarge && (
                <label className="text-danger mr-3">
                  Image is too large. Please try an image smaller than 8000x8000 pixels.
                </label>
              )}
              {dirty && (
                <div className="action-buttons">
                  <button className="btn btn-outline-cancel mr-3" disabled={saving} onClick={handleCancel}>
                    Cancel
                  </button>
                  <button className="btn btn-primary" disabled={saving} onClick={handleSave}>Save Media</button>
                </div>
              )}
            </div>
            {validationErrors && (
              <div className="errors-container alert alert-warning mt-3">
                <p><b>Whoops</b> - There were errors in your input that prevented the property from saving.</p>
                <ul>
                  {validationErrors.map((error, index) => {
                    return (
                      <li key={index}>{error.fullMessage}</li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>

          <div className="tab-content top-buffer-sm media-content">
            <MediaPhotoForm
              basePhotoFormProps={initialData.photoProps}
              bulkDownloadPhotosLink={project.bulkDownloadPhotosLink}
              name="property-photo-form"
              photos={photoAttachments}
              syndication={syndication}
              onPhotoChange={handlePhotoChange}
            />
            {
              showVisualMediaEmbeds() &&
              <VisualMediaForm
                customerEmbeds={visualMediaEmbedsForCustomer}
                embeds={visualMediaEmbeds}
                ovalroomGroupAdmin={initialData.ovalroomGroupAdmin}
                parentName="property"
                visualMediaTypeOptions={initialData.visualMediaTypeOptions}
                onCustomerEmbedsChange={handleVisualMediaForCustomerChange}
                onEmbedsChange={handleVisualMediaChange}
              />
            }
            {project.leaseChecked &&
              <LeaseSpacesMedia
                leaseSpacesMedia={leaseSpacesMedia}
                ovalroomGroupAdmin={initialData.ovalroomGroupAdmin}
                project={project}
                syndication={syndication}
                visualMediaTypeOptions={initialData.visualMediaTypeOptions}
                onCustomerVisualMediaChange={handleVisualMediaForCustomerForLeaseChange}
                onMediaLinksChange={handleLeaseMediaLinksChange}
                onPhotoChange={handleLeasePhotoChange}
                onVisualMediaChange={handleVisualMediaForLeaseChange}
              />
            }
            <MediaLinks mediaLinksFormFields={mediaLinksFormFields} onFormChange={handleMediaLinkFormChange}/>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

MediaTab.propTypes = {
  airbrake: PropTypes.shape({
    projectId: PropTypes.string,
    apiKey: PropTypes.string,
    environment: PropTypes.string.isRequired
  }).isRequired,
  hideActionIndicator: PropTypes.func.isRequired,
  project: ProjectPropTypes.isRequired,
  showActionIndicator: PropTypes.func.isRequired,
  syndication: PropTypes.bool.isRequired,
  updateVisualMedia: PropTypes.func.isRequired,
  visualMediaData: MediaPropTypes.isRequired
};

const mapStateToProps = (state) => {
  return {
    airbrake: state.airbrake,
    project: state.project,
    visualMediaData: state.visualMediaData
  };
};

const mapDispatchToProps = dispatch => ({
  hideActionIndicator: key => dispatch(hideActionIndicator(key)),
  showActionIndicator: key => dispatch(showActionIndicator(key)),
  updateVisualMedia: data => dispatch({ type: UPDATE_VISUAL_MEDIA, payload: data }),
});

export default connect(mapStateToProps, mapDispatchToProps)(MediaTab);
